// MatchmakingOverlay.jsx
import React, { useState, useEffect } from 'react';
import { Swords, X, Crown, Zap, Award, Package, Link2, Copy, Check } from 'lucide-react';
import { PHASE } from './hooks/useBattle';
import { Avatar } from './battleUtils';
import './MatchmakingOverlay.css';

// ─── Duration options ─────────────────────────────────────────────────────────
const DURATIONS = [
  { id: '2min',  label: '2 min',  desc: 'Fast fire', badge: 'Popular', disabled: false },
  { id: '5min',  label: '5 min',  desc: 'Standard',  badge: null,      disabled: false },
  { id: '20min', label: '20 min', desc: 'Deep work', badge: null,      disabled: false },
  { id: '1day',  label: '1 day',  desc: 'Coming soon', badge: null,    disabled: true  },
];

const CATEGORIES = ['Any', 'Coding', 'Writing', 'Analysis', 'Reasoning'];

const MODES = [
  { id: 'ranked', label: 'Ranked' },
  { id: 'friend', label: 'Challenge Friend' },
];

const TIERS = [
  { min: 2000, label: 'Prompt Legend',    color: '#F59E0B', icon: <Crown size={12} /> },
  { min: 1800, label: 'Prompt Master',    color: '#8B5CF6', icon: <Crown size={12} /> },
  { min: 1600, label: 'Prompt Architect', color: '#06B6D4', icon: <Zap size={12} />   },
  { min: 1400, label: 'Prompt Engineer',  color: '#10B981', icon: <Award size={12} /> },
  { min: 1200, label: 'Apprentice',       color: '#3B82F6', icon: <Award size={12} /> },
  { min: 0,    label: 'Rookie',           color: '#6B7280', icon: <Package size={12} /> },
];

const getTier = (elo) => TIERS.find(t => elo >= t.min) || TIERS[TIERS.length - 1];

// ─── Elapsed timer ────────────────────────────────────────────────────────────
const useElapsed = (active) => {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    if (!active) { setSecs(0); return; }
    const t = setInterval(() => setSecs(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [active]);
  return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
};

// ─── Shared duration + category picker ───────────────────────────────────────
const DurationCategoryPicker = ({ selectedDur, setSelectedDur, selectedCat, setSelectedCat }) => (
  <>
    <div className="mo-section-label">Duration</div>
    <div className="mo-dur-grid">
      {DURATIONS.map(d => (
        <button
          key={d.id}
          className={`mo-dur-btn ${selectedDur === d.id ? 'active' : ''} ${d.disabled ? 'disabled' : ''}`}
          onClick={() => !d.disabled && setSelectedDur(d.id)}
          disabled={d.disabled}
        >
          <span className="mo-dur-time">{d.label}</span>
          <span className="mo-dur-desc">{d.desc}</span>
          {d.badge && <span className="mo-dur-badge">{d.badge}</span>}
        </button>
      ))}
    </div>

    <div className="mo-section-label" style={{ marginTop: 16 }}>Category</div>
    <div className="mo-cat-row">
      {CATEGORIES.map(c => (
        <button
          key={c}
          className={`mo-cat-chip ${selectedCat === c ? 'active' : ''}`}
          onClick={() => setSelectedCat(c)}
        >
          {c}
        </button>
      ))}
    </div>
  </>
);

// ─── Challenge Friend panel ────────────────────────────────────────────────────
const ChallengeFriendPanel = ({
  challengeCode, challengeError, createChallenge, cancelChallenge,
  selectedDur, setSelectedDur, selectedCat, setSelectedCat,
}) => {
  const [copied, setCopied] = useState(false);

  const link = challengeCode ? `${window.location.origin}/play/${challengeCode}` : '';

  const handleCopy = () => {
    if (!link) return;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Not yet generated — show duration picker + generate button
  if (!challengeCode) {
    return (
      <div className="mo-friend-panel">
        <p className="mo-friend-desc">
          Generate a link, send to a friend — no queue, no ELO.
        </p>

        <DurationCategoryPicker
          selectedDur={selectedDur}
          setSelectedDur={setSelectedDur}
          selectedCat={selectedCat}
          setSelectedCat={setSelectedCat}
        />

        <button className="mo-btn-find" onClick={() => createChallenge(selectedDur)}>
          <Link2 size={15} /> Generate Challenge Link
        </button>
        {challengeError && <p className="mo-friend-error">{challengeError}</p>}
      </div>
    );
  }

  // Link generated — show it + waiting state
  return (
    <div className="mo-friend-panel">
      <p className="mo-friend-waiting-label">Share this link with your friend:</p>
      <div className="mo-friend-link-box">
        <span className="mo-friend-link-text">{link}</span>
        <button className="mo-friend-copy-btn" onClick={handleCopy}>
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <p className="mo-friend-waiting">
        <span className="mo-searching-dot" /> Waiting for your friend to join… (link expires in 10 min)
      </p>
      <button className="mo-btn-cancel" onClick={cancelChallenge}>
        <X size={13} /> Cancel
      </button>
    </div>
  );
};

// ─── MAIN ─────────────────────────────────────────────────────────────────────
const MatchmakingOverlay = ({
  phase, myUsername, myEntry,
  joinQueue, cancelQueue, navigate, user,
  challengeCode, challengeError, createChallenge, cancelChallenge,
}) => {
  const [selectedDur,  setSelectedDur]  = useState('2min');
  const [selectedMode, setSelectedMode] = useState('ranked');
  const [selectedCat,  setSelectedCat]  = useState('Any');
  const [searching,    setSearching]    = useState(phase === PHASE.QUEUING);

  const elapsed = useElapsed(searching);

  useEffect(() => { setSearching(phase === PHASE.QUEUING); }, [phase]);

  const handleFind = () => {
    if (!user) { navigate('/login'); return; }
    setSearching(true);
    joinQueue({ duration: selectedDur, mode: selectedMode, category: selectedCat });
  };

  const handleCancel = () => {
    setSearching(false);
    if (selectedMode === 'friend' && challengeCode) {
      cancelChallenge();
    } else {
      cancelQueue();
    }
  };

  const myElo     = myEntry?.elo ?? 1000;
  const myWins    = myEntry?.wins ?? 0;
  const myBattles = myEntry?.battles ?? 0;
  const myLosses  = myBattles - myWins;
  const winRate   = myBattles > 0 ? Math.round((myWins / myBattles) * 100) : 0;
  const tier      = getTier(myElo);

  // ── SEARCHING STATE ───────────────────────────────────────────────────────────
  if (searching) {
    if (selectedMode === 'friend') {
      return (
        <div className="mo-backdrop">
          <div className="mo-modal mo-modal-searching">
            <ChallengeFriendPanel
              challengeCode={challengeCode}
              challengeError={challengeError}
              createChallenge={createChallenge}
              cancelChallenge={handleCancel}
              selectedDur={selectedDur}
              setSelectedDur={setSelectedDur}
              selectedCat={selectedCat}
              setSelectedCat={setSelectedCat}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="mo-backdrop">
        <div className="mo-modal mo-modal-searching">

          <div className="mo-vs-arena">
            {/* Your card */}
            <div className="mo-vs-card mo-vs-you">
              <div className="mo-vs-card-bg" />
              <div className="mo-vs-avatar-wrap">
                <div className="mo-vs-avatar-ring" style={{ borderColor: tier.color }} />
                <Avatar seed={myUsername} size={56} />
                <div className="mo-vs-avatar-glow" style={{ background: tier.color }} />
              </div>
              <div className="mo-vs-name">{myUsername}</div>
              <div className="mo-vs-elo" style={{ color: tier.color }}>{myElo}</div>
              <div className="mo-vs-tier-badge" style={{ background: tier.color + '18', color: tier.color, borderColor: tier.color + '30' }}>
                {tier.icon}&nbsp;{tier.label}
              </div>
              <div className="mo-vs-record">{myWins}W · {myLosses}L</div>
            </div>

            {/* VS divider */}
            <div className="mo-vs-divider">
              <span className="mo-clash-emoji">⚔️</span>
              <span className="mo-vs-label">VS</span>
            </div>

            {/* Opponent unknown card */}
            <div className="mo-vs-card mo-vs-opp">
              <div className="mo-vs-card-bg mo-vs-card-bg-opp" />
              <div className="mo-vs-avatar-wrap">
                <div className="mo-vs-unknown-ring-outer" />
                <div className="mo-vs-unknown">?</div>
              </div>
              <div className="mo-vs-name mo-vs-name-searching">
                <span className="mo-dot-1">·</span>
                <span className="mo-dot-2">·</span>
                <span className="mo-dot-3">·</span>
              </div>
              <div className="mo-vs-elo mo-vs-elo-unknown">—</div>
              <div className="mo-vs-searching-badge">
                <span className="mo-searching-dot" />
                Searching
              </div>
              <div className="mo-vs-record mo-vs-record-unknown">± 150 ELO range</div>
            </div>
          </div>

          {/* Timer */}
          <div className="mo-search-timer">
            <span className="mo-search-timer-label">Wait time</span>
            <span className="mo-search-timer-val">{elapsed}</span>
          </div>

          <p className="mo-bot-notice">
            No match found in 15s? A bot steps in automatically.
          </p>

          <button className="mo-btn-cancel" onClick={handleCancel}>
            <X size={13} /> Cancel
          </button>
        </div>
      </div>
    );
  }

  // ── IDLE / CONFIG STATE ────────────────────────────────────────────────────
  return (
    <div className="mo-backdrop">
      <div className="mo-modal">

        {/* Header */}
        <div className="mo-modal-header">
          <div className="mo-header-icon"><Swords size={16} /></div>
          <span className="mo-modal-title">Find a Battle</span>
        </div>

        {/* Your ELO hero card */}
        <div className="mo-player-card">
          <div className="mo-player-card-left">
            <Avatar seed={myUsername} size={44} />
            <div className="mo-player-info">
              <span className="mo-player-name">{myUsername}</span>
              <span className="mo-player-tier" style={{ color: tier.color }}>
                {tier.icon}&nbsp;{tier.label}
              </span>
            </div>
          </div>
          <div className="mo-player-stats">
            <div className="mo-player-stat">
              <span className="mo-player-stat-val" style={{ color: tier.color }}>{myElo}</span>
              <span className="mo-player-stat-label">ELO</span>
            </div>
            <div className="mo-player-stat-divider" />
            <div className="mo-player-stat">
              <span className="mo-player-stat-val">{myWins}W·{myLosses}L</span>
              <span className="mo-player-stat-label">Record</span>
            </div>
            {myBattles > 0 && (
              <>
                <div className="mo-player-stat-divider" />
                <div className="mo-player-stat">
                  <span className="mo-player-stat-val">{winRate}%</span>
                  <span className="mo-player-stat-label">Win rate</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Mode tabs */}
        <div className="mo-tabs">
          {MODES.map(m => (
            <button
              key={m.id}
              className={`mo-tab ${selectedMode === m.id ? 'active' : ''}`}
              onClick={() => setSelectedMode(m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>

        <div className="mo-body">
          {selectedMode === 'friend' ? (
            <ChallengeFriendPanel
              challengeCode={null}
              challengeError={challengeError}
              createChallenge={(durationId) => {
                if (!user) { navigate('/login'); return; }
                setSearching(true);
                createChallenge(durationId);
              }}
              cancelChallenge={handleCancel}
              selectedDur={selectedDur}
              setSelectedDur={setSelectedDur}
              selectedCat={selectedCat}
              setSelectedCat={setSelectedCat}
            />
          ) : (
            <>
              <DurationCategoryPicker
                selectedDur={selectedDur}
                setSelectedDur={setSelectedDur}
                selectedCat={selectedCat}
                setSelectedCat={setSelectedCat}
              />

              {!user ? (
                <button className="mo-btn-find" onClick={() => navigate('/login')}>
                  Log in to battle
                </button>
              ) : (
                <button className="mo-btn-find" onClick={handleFind}>
                  <Swords size={15} />
                  Find opponent
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MatchmakingOverlay;